import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { sessionsByProjectGroupSelector } from "../../../stats/state/statsSlice";
import type { DateRange } from "../DateRangePicker";
import { getSessionDate } from "../../utils/dateUtils";

interface SessionsPerMonthChartProps {
  dateRange: DateRange;
  projectId?: number;
  projectGroupId?: number;
}

// Color palette for parcours (based on app's color scheme: green, blue, purple)
const PARCOURS_COLORS = [
  "#00c889", // Green 500 (primary)
  "#0c1e59", // Blue 500 (dark blue)
  "#563cff", // Purple 500
  "#00b47b", // Green 600
  "#55628b", // Blue 400
  "#8977ff", // Purple 400
  "#009667", // Green 700
  "#9ea5bd", // Blue 300
  "#4d36e6", // Purple 600
  "#4dd9ac", // Green 400
];

interface MonthData {
  month: number;
  year: number;
  label: string;
  fullLabel: string;
  sessions: number;
  [key: string]: number | string; // For parcours-specific counts
}

interface ParcoursInfo {
  id: number;
  name: string;
  color: string;
}

// Custom tooltip component for stacked bars
interface CustomStackedTooltipProps {
  active?: boolean;
  payload?: Array<{
    value?: number;
    dataKey?: string;
    color?: string;
    name?: string;
    payload?: MonthData;
  }>;
  label?: string;
  parcoursInfoList: ParcoursInfo[];
  t: (key: string) => string;
}

const CustomStackedTooltip = ({
  active,
  payload,
  label,
  parcoursInfoList,
  t,
}: CustomStackedTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;

  // Get the full month label from the first payload item
  const monthData = payload[0]?.payload as MonthData;
  const fullLabel = monthData?.fullLabel || label;

  // Calculate total
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  // Sort by value descending and filter out zeros
  const sortedPayload = [...payload]
    .filter((entry) => (entry.value || 0) > 0)
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[180px]"
      style={{ maxHeight: "300px", overflowY: "auto" }}
    >
      <p className="font-semibold text-gray-900 mb-2 border-b pb-1">{fullLabel}</p>
      {sortedPayload.map((entry, index) => {
        const parcoursInfo = parcoursInfoList.find((p) => `parcours_${p.id}` === entry.dataKey);
        return (
          <div key={index} className="flex items-center gap-2 py-0.5">
            <span
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm truncate flex-1" style={{ color: entry.color }}>
              {parcoursInfo?.name || entry.name}
            </span>
            <span className="text-sm font-medium text-gray-700">{entry.value}</span>
          </div>
        );
      })}
      {sortedPayload.length > 0 && (
        <div className="border-t mt-2 pt-1 flex justify-between">
          <span className="text-sm font-medium text-gray-600">{t("statsNew.charts.total")}</span>
          <span className="text-sm font-bold text-gray-900">{total}</span>
        </div>
      )}
    </div>
  );
};

const SessionsPerMonthChart = ({
  dateRange,
  projectId,
  projectGroupId,
}: SessionsPerMonthChartProps) => {
  const { t } = useTranslation();
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);

  // Determine if we should show stacked bars (only in global view)
  const showStackedBars = !projectGroupId && !projectId;

  // State for the end date of the 12-month view window
  const [viewEndDate, setViewEndDate] = useState(new Date());

  // Reset view when dateRange changes
  useEffect(() => {
    setViewEndDate(new Date());
  }, [dateRange]);

  const monthLabels = useMemo(
    () => [
      t("pages.statistics.months.january"),
      t("pages.statistics.months.february"),
      t("pages.statistics.months.march"),
      t("pages.statistics.months.april"),
      t("pages.statistics.months.may"),
      t("pages.statistics.months.june"),
      t("pages.statistics.months.july"),
      t("pages.statistics.months.august"),
      t("pages.statistics.months.september"),
      t("pages.statistics.months.october"),
      t("pages.statistics.months.november"),
      t("pages.statistics.months.december"),
    ],
    [t],
  );

  // Get list of parcours with their session counts (for determining top parcours)
  const parcoursSessionCounts = useMemo(() => {
    if (!showStackedBars) return new Map<number, { name: string; count: number }>();

    const counts = new Map<number, { name: string; count: number }>();

    sessionsByProjectGroup.forEach((group) => {
      let groupCount = 0;

      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);

          // Apply date range filter
          if (dateRange.startDate && sessionDate < dateRange.startDate) return;
          if (dateRange.endDate && sessionDate > dateRange.endDate) return;

          groupCount++;
        });
      });

      if (groupCount > 0) {
        counts.set(group.project_group_id, {
          name: group.project_group_name,
          count: groupCount,
        });
      }
    });

    return counts;
  }, [sessionsByProjectGroup, dateRange, showStackedBars]);

  // Get top parcours (limit to 10 for readability)
  const parcoursInfoList = useMemo((): ParcoursInfo[] => {
    if (!showStackedBars) return [];

    const sortedParcours = Array.from(parcoursSessionCounts.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10);

    return sortedParcours.map(([id, { name }], index) => ({
      id,
      name,
      color: PARCOURS_COLORS[index % PARCOURS_COLORS.length],
    }));
  }, [parcoursSessionCounts, showStackedBars]);

  const chartData = useMemo(() => {
    // Generate 12 months ending at viewEndDate
    const months: MonthData[] = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - i, 1);
      const monthData: MonthData = {
        month: date.getMonth(),
        year: date.getFullYear(),
        label: `${monthLabels[date.getMonth()].slice(0, 3)} ${date
          .getFullYear()
          .toString()
          .slice(-2)}`,
        fullLabel: `${monthLabels[date.getMonth()]} ${date.getFullYear()}`,
        sessions: 0,
      };

      // Initialize parcours counts if showing stacked bars
      if (showStackedBars) {
        parcoursInfoList.forEach((parcours) => {
          monthData[`parcours_${parcours.id}`] = 0;
        });
      }

      months.push(monthData);
    }

    // Count sessions per month (and per parcours if stacked)
    sessionsByProjectGroup.forEach((group) => {
      if (projectGroupId && group.project_group_id !== projectGroupId) return;

      const parcoursKey = `parcours_${group.project_group_id}`;
      const isTrackedParcours = parcoursInfoList.some((p) => p.id === group.project_group_id);

      group.projects.forEach((project) => {
        if (projectId && project.project_id !== projectId) return;

        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);

          // Apply date range filter
          if (dateRange.startDate && sessionDate < dateRange.startDate) return;
          if (dateRange.endDate && sessionDate > dateRange.endDate) return;

          const sessionMonth = sessionDate.getMonth();
          const sessionYear = sessionDate.getFullYear();

          const monthData = months.find((m) => m.month === sessionMonth && m.year === sessionYear);
          if (monthData) {
            monthData.sessions++;

            // Add to parcours-specific count if showing stacked bars
            if (showStackedBars && isTrackedParcours) {
              monthData[parcoursKey] = ((monthData[parcoursKey] as number) || 0) + 1;
            }
          }
        });
      });
    });

    return months;
  }, [
    sessionsByProjectGroup,
    dateRange,
    projectId,
    projectGroupId,
    monthLabels,
    viewEndDate,
    showStackedBars,
    parcoursInfoList,
  ]);

  const hasData = chartData.some((d) => d.sessions > 0);

  // Navigation handlers
  const goToPrevMonth = () => {
    setViewEndDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    const next = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() + 1, 1);
    if (next <= new Date()) {
      setViewEndDate(next);
    }
  };

  const goToPrevYear = () => {
    setViewEndDate((prev) => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
  };

  const goToNextYear = () => {
    const next = new Date(viewEndDate.getFullYear() + 1, viewEndDate.getMonth(), 1);
    if (next <= new Date()) {
      setViewEndDate(next);
    }
  };

  const canGoNext =
    new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() + 1, 1) <= new Date();

  // Get display period text
  const periodText = useMemo(() => {
    const startDate = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - 11, 1);
    const startStr = `${monthLabels[startDate.getMonth()].slice(0, 3)} ${startDate.getFullYear()}`;
    const endStr = `${monthLabels[viewEndDate.getMonth()].slice(
      0,
      3,
    )} ${viewEndDate.getFullYear()}`;
    return `${startStr} - ${endStr}`;
  }, [viewEndDate, monthLabels]);

  return (
    <div className="h-full flex flex-col">
      {/* Navigation controls */}
      <div className="flex-shrink-0 flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          {/* Previous year */}
          <button
            onClick={goToPrevYear}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={t("statsNew.charts.prevYear")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
              />
            </svg>
          </button>
          {/* Previous month */}
          <button
            onClick={goToPrevMonth}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={t("statsNew.charts.prevMonth")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>

        <span className="text-sm text-gray-500 font-medium">{periodText}</span>

        <div className="flex items-center gap-1">
          {/* Next month */}
          <button
            onClick={goToNextMonth}
            disabled={!canGoNext}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={t("statsNew.charts.nextMonth")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {/* Next year */}
          <button
            onClick={goToNextYear}
            disabled={!canGoNext}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={t("statsNew.charts.nextYear")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Chart */}
      {!hasData ? (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          {t("statsNew.charts.noData")}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%" className="flex-1">
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              allowDecimals={false}
              width={30}
            />
            {showStackedBars ? (
              <>
                <Tooltip
                  content={(props: unknown) => (
                    <CustomStackedTooltip
                      {...(props as CustomStackedTooltipProps)}
                      parcoursInfoList={parcoursInfoList}
                      t={t}
                    />
                  )}
                  cursor={{ fill: "rgba(0, 200, 137, 0.1)" }}
                />
                {/* Render bars in reverse order so first parcours appears on top */}
                {[...parcoursInfoList].reverse().map((parcours) => (
                  <Bar
                    key={parcours.id}
                    dataKey={`parcours_${parcours.id}`}
                    name={parcours.name}
                    stackId="parcours"
                    fill={parcours.color}
                    radius={[0, 0, 0, 0]}
                    maxBarSize={40}
                  />
                ))}
              </>
            ) : (
              <>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                  cursor={{ fill: "rgba(0, 200, 137, 0.1)" }}
                  formatter={(value: number) => [value, t("statsNew.charts.sessions")]}
                />
                <Bar dataKey="sessions" fill="#00c889" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default SessionsPerMonthChart;
